import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, MessageSquareWarning } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Nouvelle", "En cours", "Résolue", "Fermée"];
const PRIORITIES = ["Basse", "Moyenne", "Haute"];
const statusColor = {
  Nouvelle: "bg-blue-50 text-blue-700 border-blue-200",
  "En cours": "bg-amber-50 text-amber-700 border-amber-200",
  Résolue: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Fermée: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function Complaints() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ client_id: "", order_id: "", description: "", priority: "Moyenne", assignee: "" });

  const load = useCallback(() => { setLoading(true); api.get(`/complaints${filter !== "all" ? `?status=${filter}` : ""}`).then((r) => setItems(r.data)).finally(() => setLoading(false)); }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/orders?page_size=100").then((r) => setOrders(r.data.items));
  }, []);

  const save = async () => {
    try {
      await api.post("/complaints", { ...form, client_id: form.client_id || null, order_id: form.order_id || null });
      toast.success("Réclamation créée");
      setShowForm(false); setForm({ client_id: "", order_id: "", description: "", priority: "Moyenne", assignee: "" }); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const updateStatus = async (c, status) => {
    try { await api.put(`/complaints/${c.id}`, { status }); toast.success("Statut mis à jour"); load(); setDetail(null); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl sm:text-3xl font-bold font-heading">Réclamations</h1><p className="text-slate-500 text-sm mt-1">Suivi du service client</p></div>
        <Button data-testid="complaints-new-btn" onClick={() => setShowForm(true)} className="bg-brand-600 hover:bg-brand-700"><Plus className="w-4 h-4 mr-2" /> Nouvelle</Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["all", ...STATUSES].map((s) => (
          <button key={s} data-testid={`complaints-filter-${s}`} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"}`}>{s === "all" ? "Toutes" : s}</button>
        ))}
      </div>

      {loading ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div> : (
        <div className="space-y-3">
          {items.map((c) => (
            <button key={c.id} data-testid={`complaint-card-${c.id}`} onClick={() => setDetail(c)} className="w-full text-left bg-white border rounded-2xl p-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2"><MessageSquareWarning className="w-5 h-5 text-amber-500" /><div><div className="font-medium">{c.client_name || "Client inconnu"}</div><div className="text-xs text-slate-400">{c.order_number ? `Cmd ${c.order_number} · ` : ""}{fmtDateTime(c.created_at)}</div></div></div>
                <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border ${statusColor[c.status]}`}>{c.status}</span>
              </div>
              <p className="text-sm text-slate-600 mt-2 line-clamp-2">{c.description}</p>
              <div className="text-xs text-slate-400 mt-2">Priorité : {c.priority} · Responsable : {c.assignee || "—"}</div>
            </button>
          ))}
          {items.length === 0 && <p className="text-slate-400 text-center py-10">Aucune réclamation</p>}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Nouvelle réclamation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="cf-client" className="mt-1"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Commande liée</Label>
              <Select value={form.order_id} onValueChange={(v) => setForm({ ...form, order_id: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Aucune" /></SelectTrigger>
                <SelectContent>{orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_number} - {o.client_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description *</Label><Textarea data-testid="cf-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priorité</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Responsable</Label><Input value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} className="mt-1" /></div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2"><Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button><Button data-testid="cf-submit" onClick={save} className="bg-brand-600 hover:bg-brand-700">Créer</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent>
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-heading">Réclamation</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <p className="bg-slate-50 rounded-xl p-3">{detail.description}</p>
                <div>Client : {detail.client_name || "—"} · Priorité : {detail.priority}</div>
                <div>
                  <Label>Changer le statut</Label>
                  <div className="flex gap-2 flex-wrap mt-1">
                    {STATUSES.map((s) => <button key={s} data-testid={`complaint-setstatus-${s}`} onClick={() => updateStatus(detail, s)} className={`px-3 py-1.5 rounded-lg text-sm border ${detail.status === s ? "bg-brand-600 text-white border-brand-600" : "bg-white border-slate-200"}`}>{s}</button>)}
                  </div>
                </div>
                <div>
                  <Label>Historique</Label>
                  <div className="space-y-1.5 mt-1">
                    {(detail.history || []).map((h, i) => (<div key={`${h.at}-${i}`} className="text-xs text-slate-500 flex gap-2"><span>{fmtDateTime(h.at)}</span><span>·</span><span>{h.note} ({h.user_name})</span></div>))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
